// Aesthetic: Utilitarian lookup strip - slate, tabular numbers, one accent on
// the chosen row. This sits beside a document the clerk is about to change, so
// it stays quiet and shows exactly what will be written to.
import { useState, useCallback, useEffect } from 'react';
import { Search, Loader2, FileText, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';

const prodBase = process.env.REACT_APP_API_BASE;
const grabInvoicesRoute = process.env.REACT_APP_GRAB_INVOICES || '/api/grabinvoices';

// V22966_SCB_2023147_11.01.2023[_FA].pdf - the one archive filename contract.
// Mirrored from src/lib/naming.js; only the display fields are needed here.
function parseArchiveName(name) {
  const base = String(name || '').replace(/\.pdf$/i, '');
  const m = /^V(\d+)_([A-Za-z0-9.-]+)_([A-Za-z0-9.-]+)_(\d{2}\.\d{2}\.\d{4})(_FA)?$/i.exec(base);
  if (!m) return null;
  return { voucher: m[1], vendor: m[2], invoice: m[3], date: m[4], fixedAsset: Boolean(m[5]) };
}

/**
 * Finds an archived invoice to attach a slip to.
 *
 * The clerk reaches for this in two situations: the PO never resolved, or it
 * resolved to the wrong voucher. Both mean the automatic answer is not
 * trustworthy here, so this shows the real filename and makes them pick it -
 * there is no "best guess" row and nothing is preselected.
 */
export function ArchiveSearch({ initialQuery = '', selectedId, onSelect, className }) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  // A new slip means a new vendor to look under; keep the box in step with it.
  useEffect(() => {
    setQuery(initialQuery);
    setResults([]);
    setSearched(false);
    setError(null);
  }, [initialQuery]);

  const run = useCallback(async (term) => {
    const q = String(term ?? '').trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${prodBase}${grabInvoicesRoute}?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      if (!res.ok || data.success === false) {
        throw new Error(data.details || data.error || `Search failed (${res.status})`);
      }
      setResults(Array.isArray(data.files) ? data.files : []);
      setSearched(true);
    } catch (err) {
      setError(err.message || 'The archive could not be searched.');
      setResults([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          run(query);
        }}
      >
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Voucher, vendor, or invoice number"
          aria-label="Search the archive"
          className="h-8 text-xs"
        />
        <Button type="submit" size="sm" variant="outline" className="h-8 shrink-0 px-3 text-[11px]" disabled={loading}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          <span className="ml-1">Search</span>
        </Button>
      </form>

      {error && (
        <p className="text-[11px] text-amber-700 dark:text-amber-500">{error}</p>
      )}

      {searched && !loading && results.length === 0 && !error && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Nothing in the archive matches that. If the invoice has not been filed yet, leave the
          slip here - it will attach itself when the invoice is approved.
        </p>
      )}

      {results.length > 0 && (
        <div className="max-h-48 overflow-y-auto rounded border border-slate-200 dark:border-slate-800">
          {results.map((file) => {
            const parsed = parseArchiveName(file.name);
            const isChosen = file.id === selectedId;
            return (
              <button
                key={file.id}
                type="button"
                onClick={() => onSelect?.(file)}
                aria-pressed={isChosen}
                className={cn(
                  'flex w-full items-center gap-2 border-b border-slate-100 px-2 py-1.5 text-left text-[11px] transition-colors last:border-b-0 dark:border-slate-800',
                  isChosen
                    ? 'bg-blue-50 text-blue-900 dark:bg-blue-950 dark:text-blue-200'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
                )}
              >
                {isChosen ? (
                  <Check className="h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-400" />
                ) : (
                  <FileText className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                )}
                {parsed ? (
                  <span className="flex min-w-0 flex-1 items-baseline gap-2">
                    <span className="font-semibold tabular-nums">V{parsed.voucher}</span>
                    <span className="truncate text-slate-600 dark:text-slate-400">{parsed.vendor}</span>
                    <span className="truncate tabular-nums text-slate-500">{parsed.invoice}</span>
                    <span className="ml-auto shrink-0 tabular-nums text-slate-400">{parsed.date}</span>
                  </span>
                ) : (
                  <span className="truncate">{file.name}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ArchiveSearch;
